import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import { observer } from 'mobx-react'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import useSearchParams from 'modules/core/hooks/useSearchParams'
import FiltersForm from 'modules/core/lib/forms/Filters'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import contractVersions from 'modules/contracts/store/collections/contractVersions'
import hasRegularAccess from 'modules/employees/lib/hasRegularAccess'
import employeesCollection from 'modules/employees/store/collections/employees'
import BulkEditTable from 'modules/job_catalog/components/BulkEditTable'
import useEmployeesWithWrongJobLevel from 'modules/job_catalog/hooks/useEmployeesWithWrongJob'
import useEmployeesWithoutJobLevel from 'modules/job_catalog/hooks/useEmployeesWithoutJobLevel'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

const Component = () => {
  const { employee } = useCurrentSession()

  return (
    <AuthorizeUser
      authorizeFn={() => {
        if (!employee) return true

        return hasRegularAccess(employee.hasRegularAccess)
      }}
    >
      <BulkEditPage />
    </AuthorizeUser>
  )
}

interface Params {
  state: 'unnasigned' | 'wrong-job-data'
  employeesIds: number[]
}

const BulkEditPage = observer(() => {
  const params = useSearchParams<Params>()
  const [localFilterByValue] = useLocalStorage(
    LOCAL_STORAGE_KEYS.employeesFilter,
    'activity___active'
  )
  const filtersForm = useMemo(
    () =>
      new FiltersForm({
        sortAttribute: 'first_name',
        sortDirection: 'asc',
        filterBy: localFilterByValue || '',
      }),
    [localFilterByValue]
  )
  const employeesToShow = employeesCollection.active.filter((x) =>
    policyAuthorizations.for('contracts.assign_jobs', x)
  )

  const { employees: employeesWithWrongJobLevel } = useEmployeesWithWrongJobLevel(
    employeesToShow,
    contractVersions
  )
  const { employees: employeesWithoutJobLevel } = useEmployeesWithoutJobLevel(
    employeesToShow,
    contractVersions
  )

  const { loading: authorizationsLoading } = useAsyncData(
    () =>
      policyAuthorizations.authorizePolicies(
        'contracts.assign_jobs',
        employeesCollection.toArray()
      ),
    []
  )

  const employees = useUnsafeMemo(() => {
    if (params.employeesIds) {
      return employeesWithoutJobLevel.filter(({ id }) => params.employeesIds.includes(id))
    } else if (params.state === 'wrong-job-data') {
      return employeesWithWrongJobLevel
    } else {
      return employeesWithoutJobLevel
    }
  }, [params.state, params.employeesIds, employeesWithWrongJobLevel, employeesWithoutJobLevel])

  return (
    <ResponsivePage.Body variant="full" loading={authorizationsLoading} error={null}>
      <BulkEditTable
        filtersForm={filtersForm}
        employees={employees}
        state={params.state}
        contractVersions={contractVersions}
      />
    </ResponsivePage.Body>
  )
})

export default Component
